import Header from "./shared/header";
import Footer from "./shared/footer";
import MobileLayout from "./shared/mobile_layout";
import { useState, useEffect } from "react";

import DesktopLayout from "./shared/desktop_layout";
import FeaturesSection from "./shared/outcomesWeDeliver/featuresSection";
import CaseStudies from "./shared/outcomesWeDeliver/CaseStudies";

function AgenticExperience() {
  const features = [
    {
      title: "What Is an AI Coworker?",
      description:
        "AI coworkers are accountable, task-owning agents with memory, context, enterprise access, and defined roles. They collaborate with humans or operate independently across digital workflows.",
      image: "https://via.placeholder.com/200x150",
      bullets: [
        {
          note: "Operate solo or in teams with runtime metrics and performance reviews",
        },
        {
          note: "Work across domains: content, analytics, campaigns, data migration",
        },
        {
          note: "Examples: social media generation, lending risk monitoring, campaign optimization, ETL code conversion",
        },
      ],
    },
    {
      title: "Strategy & Operating Model Redesign",
      description:
        "We partner with leadership teams to rethink workflows, decision rights, and performance measurement in AI-augmented organizations.",
      image: "https://via.placeholder.com/200x150",
      bullets: [
        { note: "Human + AI team structures" },
        { note: "Agent-level KPIs: productivity, accuracy, compliance" },
        {
          note: "Governance frameworks: ethics, observability, accountability",
        },
        { note: "Change management and capability uplift initiatives" },
      ],
    },
    {
      title: "OBZ GenAI Studio",
      description:
        "An enterprise-grade platform to build, deploy, and govern AI coworkers inside your AWS environment, aligned with Amazon Bedrock AgentCore.",
      image: "https://via.placeholder.com/200x150",
      bullets: [
        {
          note: "Agent Builder: low-code interface for prompts, memory, tools, goals",
        },
        {
          note: "Command Center: deployment, roles, approvals, metrics, versioning",
        },
        {
          note: "Model Context Protocol (MCP): knowledge, guardrails, evaluation, memory",
        },
        {
          note: "Strategic collaborator in AWS Generative AI Innovation Center",
        },
      ],
    },
    {
      title: "Technical Architecture",
      description:
        "Our architecture ensures enterprise-grade security, scalability, and control — with contextual intelligence and open model support.",
      image: "https://via.placeholder.com/200x150",
      bullets: [
        { note: "Security: No data leaves your VPC" },
        { note: "Compliance: ID, audit trails, approvals" },
        { note: "Scalability: version control across use cases" },
        { note: "Contextual intelligence via MCP and your existing stack" },
        {
          note: "Open ecosystem: bring your own model or use Bedrock-hosted FMs",
        },
      ],
    },
    {
      title: "Getting Started",
      description:
        "Start small and scale confidently. We offer proven pathways to implement AI coworkers within your existing organization.",
      image: "https://via.placeholder.com/200x150",
      bullets: [
        { note: "Readiness Workshops: assess workflows and use cases" },
        { note: "Agent Pilot Programs: start with 1–2 high-impact roles" },
        { note: "Architecture Blueprints tailored to your AWS stack" },
        { note: "Change Management & Training for seamless adoption" },
      ],
    },
  ];

  const caseStudiesCards = [
    {
      text: "Scaling Agent Deployment with GenAI Studio",
      description:
        "Enabled 50+ AI flows across voice and chat without heavy engineering using OBZ’s low-code studio on AWS Bedrock. Time-to-launch improved by 30–40% through memory protocols and live evaluation.",
      image:
        "/images/revenue-growth/OBZ Case Study Images Outcomes Agentic Enterprise.png",
      slug: "scaling-agents-with-genai-studio",
    },
    {
      text: "Accelerating Legacy ETL Migration with Agentic AI",
      description:
        "A multi-agent framework automated ETL code conversion, cutting migration time by 50%. Over 60% of pipelines were migrated without human input, freeing up engineers for strategic tasks.",
      image:
        "/images/revenue-growth/OBZ Case Study Images Outcomes Agentic Enterprise2.png",
      slug: "etl-migration-agentic-ai",
    },
    {
      text: "TARA: HR Talent Agent for Internal Hiring Efficiency",
      description:
        "An AI coworker integrated with Zoho Recruit and Teams to screen resumes and suggest interview questions — streamlining internal hiring workflows and boosting recruiter efficiency.",
      image:
        "/images/revenue-growth/OBZ Case Study Images Outcomes Agentic Enterprise3.png",
      slug: "tara-hr-talent-agent",
    },
  ];

  const [screenSize, setScreenSize] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => {
      setScreenSize(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const Layout = ({ children, backgroundColor }) => {
    return screenSize <= 768 ? (
      <MobileLayout backgroundColor={backgroundColor}>{children}</MobileLayout>
    ) : (
      <DesktopLayout backgroundColor={backgroundColor}>
        {children}
      </DesktopLayout>
    );
  };

  return (
    <div>
      <Layout>
        <Header color="rgba(11, 21, 76, 1)" />
      </Layout>
      <Layout backgroundColor="black">
        <div className="w-full mt-16 py-16 lg:py-10">
          <div className="w-[88%] lg:w-[80%] mx-auto">
            {/* Header Section */}
            <div className="mb-12 lg:mb-7">
              <h2 className="text-[32px] lg:text-[48px] xl:text-[56px] text-white RoobertLight leading-[120%] mb-4">
                Agentic Enterprise
              </h2>
              <h3 className="text-[20px] lg:text-[24px] xl:text-[28px] text-white RoobertMedium leading-[120%] mb-8">
                Reimagine how work gets done, with AI coworkers at the center.
              </h3>
            </div>

            {/* Body Text */}
            <div className="mb-12 lg:mb-8 space-y-5">
              <p className="text-[16px] lg:text-[18px] text-white RoobertRegular leading-[140%]">
                The future of enterprise productivity will not be powered by
                tools, dashboards, or co-pilots. It will be shaped by
                intelligent, accountable AI co-workers, digital employees that
                work alongside humans to execute tasks, drive workflows, and
                scale capability.
              </p>
              <p className="text-[16px] lg:text-[18px] text-white RoobertRegular leading-[140%]">
                At OBZ, we help you design and deploy an agentic enterprise: one
                where human and AI co-workers operate together, across
                marketing, finance, operations, customer service, IT, and
                beyond. These are not point solutions. They are long-term
                changes to your organization’s DNA.
              </p>
            </div>
            <div className="mb-12 lg:mb-5">
              <FeaturesSection features={features} />
            </div>
          </div>
        </div>
      </Layout>
      <Layout backgroundColor="black">
        <CaseStudies caseStudiesCards={caseStudiesCards} />
      </Layout>
      <Layout>
        <div className="w-full bg-fluoro-green py-16 lg:py-8 min-h-[500px] flex items-center justify-center relative">
          <div className="w-[88%] lg:w-[80%] py-5 px-5 h-full mx-auto relative bg-fluoro-green">
            <div className="mb-12 lg:mb-7 px-6">
              <h2 className="text-[32px] lg:text-[48px] xl:text-[56px] text-[#0A1931] RoobertLight leading-[120%] mb-4">
                This Is Organizational Redesign — Not Just Automation
              </h2>
              <p className="text-[16px] lg:text-[18px] text-[#0A1931] font-bolder RoobertSemiBold leading-[140%] mb-4">
                It’s not about replacing jobs. It’s about evolving the structure
                of how enterprises deliver value. AI coworkers will augment your
                workforce, not abstract it.
              </p>
              <p className="text-[16px] lg:text-[18px] text-[#0A1931] font-bolder RoobertSemiBold leading-[140%] mb-4">
                We help you move from fragmented tools to governed AI teams —
                designed for impact, accountability, and trust.
              </p>

              <p className="text-[16px] lg:text-[18px] text-[#0A1931] font-bolder RoobertSemiBold leading-[140%] mb-4">
                → Ready to optimize your ops? Let’s get started. 
              </p>
              <div className="border-r-4 border-t-4 border-[#0A1931] absolute top-0 right-0 w-1/4 h-1/2"></div>
              <div className="border-r-4 border-t-4 border-[#0A1931] absolute bottom-0 rotate-180 left-0 w-1/4 h-1/2"></div>
            </div>
          </div>
        </div>
      </Layout>
      <Layout>
        <Footer />
      </Layout>
    </div>
  );
}

export default AgenticExperience;
